<template>
	<div class="field">
		<div class="input">
			<label :for="slug" class="screen-reader-text">
				{{ label }}
			</label>

			<v-icon :slug="icon_slug" />

			<input
				v-if="slug === 'query'"
				:id="slug"
				v-model="model"
				:placeholder="placeholder"
				type="search"
				:name="slug"
				autocomplete="off"
				@input="on_input"
			/>

			<input
				v-else
				:id="slug"
				v-model="model"
				:placeholder="placeholder"
				type="text"
				autocomplete="off"
				:name="slug"
				@input="on_input"
			/>
		</div>

		<div v-if="suggestions.length > 0" class="dropdown" tabindex="0">
			<button
				v-for="suggestion of suggestions"
				:key="suggestion"
				type="button"
				class="btn"
				@click="set_value(suggestion)"
			>
				<v-icon :slug="icon_slug" />
				<span v-html="highlight(suggestion)" />
			</button>
		</div>
	</div>
</template>

<style lang="scss" scoped>
@use 'sass:math';

@use '/sass/variables' as *;
@use '/sass/toolkit' as *;

.field {
	position: relative;

	&:focus-within {
		.dropdown {
			opacity: 1;
			visibility: visible;
		}
	}
}

.input {
	transition: all 0.15s ease-in-out;
	position: relative;

	&:focus-within {
		> svg {
			fill: $orange;
		}

		input[type='text'],
		input[type='search'] {
			color: $black;
			border-color: $orange;
		}
	}

	svg {
		display: block;
		position: absolute;
		top: 50%;
		left: calc($spacer - 2px);
		width: 1.25em;
		height: 1.25em;
		transform: translateY(-50%);
		pointer-events: none;
		transition: inherit;
	}

	input[type='text'],
	input[type='search'] {
		padding-left: calc((($spacer - 2px) * 2) + 1em);
		border: 1px solid $grey-border;
		border-radius: 9999px;
		background-color: $white;
		color: $text;
		transition: inherit;
	}

	::placeholder {
		color: $grey-meta;
		opacity: 1;
	}
}

.dropdown {
	position: absolute;
	top: 100%;
	left: 0;
	width: 100%;
	padding: 1em;
	border-radius: 1em;
	border: 1px solid $grey-border;
	z-index: 99;
	background-color: $white;
	margin-top: math.div($spacer, 2);
	transition: all 0.15s ease-in-out;
	opacity: 0;
	visibility: hidden;

	.btn {
		display: flex;
		align-items: center;
		width: 100%;
		gap: math.div($spacer, 2);
		border-bottom: 1px solid $grey-border;
		margin-bottom: math.div($spacer, 2);
		padding-bottom: math.div($spacer, 2);
		cursor: pointer;
		text-align: left;

		&:last-child {
			margin-bottom: 0;
			padding-bottom: 0;
			border-bottom: none;
		}

		> svg {
			display: block;
			width: 1.25em;
			height: 1.25em;
		}

		span {
			font-size: 0.875rem;
		}

		:deep(mark) {
			background-color: transparent;
			color: $orange;
		}
	}
}
</style>

<script setup>
import { useTimeoutFn } from '@vueuse/core';
import { computed, ref } from 'vue';

import { fetch_app_data } from '../_partials/_app.js';
import VIcon from './_v-icon.vue';

function escape(string) {
	return string.replace(/[.*+?^${}()|[\]\\]/g, `\\$&`);
}

const props = defineProps({
	api_url: {
		type: String,
		required: true,
	},
	slug: {
		type: String,
		required: true,
	},
	label: {
		type: String,
		required: true,
	},
	placeholder: {
		type: String,
		required: true,
	},
});

const emit = defineEmits([`change`]);

const suggestions = ref([]);

const controller = ref(null);

const model = defineModel({
	type: String,
});

const saved_query = ref(``);

const icon_slug = computed(() => {
	switch (props.slug) {
		case `query`:
			return `magnifying-glass`;
		case `location`:
			return `location-dot`;
	}

	return ``;
});

function highlight(suggestion) {
	const regex = new RegExp(escape(saved_query.value), `i`);

	return suggestion.replace(regex, (match) => `<mark>${match}</mark>`);
}

function lookup() {
	if (controller.value) {
		controller.value.abort();
		controller.value = null;
	}

	if (model.value.length < 1) {
		return;
	}

	const data = {
		type: props.slug,
		value: model.value,
	};

	saved_query.value = model.value;

	controller.value = new AbortController();

	fetch_app_data(props.api_url, `/api/listings/suggest`, {
		method: `POST`,
		body: data,
		signal: controller.value.signal,
	})
		.then((response) => {
			suggestions.value = response || [];
		})
		.catch(() => {
			// Do nothing
		});
}

const { start: debounced_lookup, stop: cancel_lookup } = useTimeoutFn(lookup, 50, {
	immediate: false,
});

function on_input() {
	emit(`change`);

	if (controller.value) {
		controller.value.abort();
		controller.value = null;
	}

	debounced_lookup();
}

function set_value(suggestion) {
	cancel_lookup();

	if (controller.value) {
		controller.value.abort();
		controller.value = null;
	}

	model.value = suggestion;
	suggestions.value = [];

	emit(`change`);
}
</script>
